import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { throwError, Observable } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FieldDetailsComponent } from '../dialogs/field-details/field-details.component';
import { SeasonManagerService } from './season-manager.service';
import { SeasonSharedDataService } from './season-shared-data.service';
import { NavBarComponent } from '../nav-bar/nav-bar.component';
import { ChangeDetectorRef } from '@angular/core';
import { Subject } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FieldDetailsService {
  private seasonDetailCallCount = 0;
  private seasonFormData: any = null;
  private seasons: any[] = [];
  private dialogClosed$ = new Subject<any>();

  constructor(
    private dialog: MatDialog,
    private seasonManagerService: SeasonManagerService,
    private seasonSharedDataService: SeasonSharedDataService,
    private nav: NavBarComponent
  ) { }

  viewFieldDetails(fieldId: number, tenantId: number): Observable<any> {
    this.seasonDetailCallCount = 0;
    this.populateMetaSeasonDropdownDetails();
    this.populateSeasonDropdownDetails(tenantId);
    this.populateDetailField(fieldId, true);
    this.seasonSharedDataService.updateIsHiddenData(true);
    return this.dialogClosed$;
  }

  populateMetaSeasonDropdownDetails() {

    this.seasonManagerService.getSeasonMetaData().pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {

      if (response == null) {
        return;
      }

      this.seasonSharedDataService.updateCombinedData(response);

    });

  }

  private populateSeasonDropdownDetails(tenantId: number): void {
    this.seasonManagerService.getSeasonsListForDropDown(tenantId).pipe(
      catchError(error => {
        this.seasonDetailCallCount++;
        this.checkAndOpenDialog();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.seasons = response;
      this.seasonDetailCallCount++;
      this.checkAndOpenDialog();
    });
  }

  private populateDetailField(fieldId: number, viewDetailsPressed: boolean): void {
    this.nav.toggleLoading(true);
    this.seasonManagerService.getDetailsOfFieldById(fieldId).pipe(
      catchError(error => {
        this.nav.toggleLoading(false);
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.seasonFormData = response;
      if (!this.seasonFormData.detail) {
        this.seasonFormData.detail = {
          fieldName: this.seasonFormData.field.name,
          allowNotifications: this.seasonFormData.field.allowNotifications,
          fieldSize: this.seasonFormData.field.fieldSize
        };
      }
      this.seasonSharedDataService.updateSeasonDetailBaseData(this.seasonFormData);
      this.seasonSharedDataService.updateIsDisabledData(this.seasonFormData.field.isInAnotherActiveSeason);

      if (this.seasonFormData?.detail?.fieldType) {
        this.seasonDetailCallCount++;
        this.populateExtraDetail(this.seasonFormData.detail.id, this.seasonFormData.detail.fieldType, viewDetailsPressed);
      } else if (viewDetailsPressed) {
        this.seasonDetailCallCount += 2;
        this.checkAndOpenDialog();
      }
      setTimeout(() => this.nav.toggleLoading(false), 1400);
    });
  }

  private populateExtraDetail(detailId: number, fieldTypeId: number, viewDetailsPressed: boolean): void {
    this.seasonManagerService.getSeasonDetailExtraData(detailId, fieldTypeId).subscribe((response) => {
      if (response) {
        Object.assign(this.seasonFormData.detail, response);
      }
      this.seasonSharedDataService.updateSeasonDetailExtraData(response);
      if (viewDetailsPressed) {
        this.seasonDetailCallCount++;
        this.checkAndOpenDialog();
      }
    });
  }

  private checkAndOpenDialog(): void {
    if (this.seasonDetailCallCount === 3) {
      
      const dialogConfig = new MatDialogConfig();
      dialogConfig.width = 'auto';
      
      dialogConfig.data = {
          seasonFormData: this.seasonFormData,
          seasons: this.seasons,
          fieldId: this.seasonFormData.field.id
        }

      const dialogRef = this.dialog.open(FieldDetailsComponent, dialogConfig); 

      dialogRef.afterClosed().subscribe((res: any) => {
        if (res) this.handleDialogResult(res);
      });
    }
  }

  private handleDialogResult(res: any): void {
    if (res === 'seasonLinked') {
      this.seasonDetailCallCount = 1;
      this.populateDetailField(this.seasonFormData.field.id, true);
    } else if (res._alreadySaved) {
      // Data was already persisted by the dialog — just notify subscribers to refresh
      this.dialogClosed$.next(res);
    } else {
      this.nav.toggleLoading(true);
      if (this.seasonFormData.season == null) {
        this.updateFieldNameAllowNotification(res);
      } else {
        this.updateSeasonDetails(res);
      }
    }
  }

  private updateFieldNameAllowNotification(res: any): void {
    const detail = {
      fieldId: this.seasonFormData.field.id,
      fieldName: res.detail.fieldName || this.seasonFormData.field.name,
      allowNotification: res.detail.allowNotifications || false
    };

    this.seasonManagerService.updateFieldNameAllowNotification(detail.fieldId, detail.fieldName, detail.allowNotification)
      .subscribe({
        next: () => {
          this.seasonFormData.field.name = res.detail.fieldName;
          this.nav.toggleLoading(false);
          this.dialogClosed$.next(res);
        },
        error:() => this.nav.toggleLoading(false)
      });
  }

  private updateSeasonDetails(res: any): void {
    const definitions = res.details?.length > 0 ? res.details : (res.detail ? [res.detail] : []);
    if (definitions.length === 0) return;

    definitions.forEach((def: any) => {
      const seasonDetails = { ...def };
      ['fieldId', 'id', 'grainFeed', 'vegetables', 'vineyard', 'fruit'].forEach(key => delete seasonDetails[key]);

      this.seasonManagerService.updateSeasonFieldBaseDetails(def.id, seasonDetails).subscribe({
        error: () => this.nav.toggleLoading(false)
      });

      this.updateFieldTypeSpecificDetailsForDef(def);
    });

    // Update local field name so the card reflects the change immediately
    const updatedName = definitions[0]?.fieldName;
    if (updatedName && this.seasonFormData.field) {
      this.seasonFormData.field.name = updatedName;
    }

    this.seasonFormData.detail = this.seasonManagerService.convertDataForFE(definitions[0]);
    this.nav.toggleLoading(false);
    this.dialogClosed$.next(res);
  }

  private updateFieldTypeSpecificDetailsForDef(def: any): void {
    const actions = {
      1: () => this.seasonManagerService.postSeasonDetailsGrainFeed(def.id, def.grainFeed),
      2: () => this.seasonManagerService.postSeasonDetailsVegetables(def.id, def.vegetables),
      3: () => this.seasonManagerService.postSeasonDetailsVineyard(def.id, def.vineyard),
      4: () => this.seasonManagerService.postSeasonDetailsFruit(def.id, def.fruit)
    };

    const action = actions[def.fieldTypeId];
    if (action) {
      action().subscribe(() => {}, () => {});
    }
  }
}
